import type {BranchPickerBranch} from '@github-ui/item-picker-branch-relay/BranchPicker'
import {commitMutation, type Environment, graphql} from 'react-relay'

import type {linkBranchesMutation, linkBranchesMutation$rawResponse} from './__generated__/linkBranchesMutation.graphql'

type LinkBranchesMutationParams = {
  environment: Environment
  input: {baseIssueId: string; linkingIds: string[]}
  onError: (error: Error) => void
  linkingBranches?: BranchPickerBranch[]
}

function buildOptimisticResponse(
  baseIssueId: string,
  linkingBranches: BranchPickerBranch[],
): linkBranchesMutation$rawResponse {
  return {
    linkBranches: {
      issue: {
        id: baseIssueId,
        linkedBranches: {
          nodes: linkingBranches.map(branch => ({
            id: `optimistic-linked-branch-${branch.id}`,
            ref: {
              __typename: 'Ref' as const,
              id: branch.id,
              name: branch.name,
              target: branch.target
                ? {
                    oid: branch.target.oid,
                    id: branch.target.id,
                    __typename: 'Commit',
                  }
                : null,
              repository: {
                id: branch.repository.id,
                nameWithOwner: branch.repository.nameWithOwner ?? '',
                defaultBranchRef: branch.repository.defaultBranchRef
                  ? {
                      name: branch.repository.defaultBranchRef.name,
                      id: branch.repository.defaultBranchRef.id,
                      target: branch.repository.defaultBranchRef.target
                        ? {
                            oid: branch.repository.defaultBranchRef.target.oid,
                            id: branch.repository.defaultBranchRef.target.id,
                            __typename: 'Commit',
                          }
                        : null,
                      associatedPullRequests: {
                        totalCount: branch.repository.defaultBranchRef.associatedPullRequests.totalCount,
                      },
                    }
                  : null,
              },
              associatedPullRequests: {
                totalCount: branch.associatedPullRequests.totalCount,
              },
            },
          })),
        },
      },
    },
  }
}

export function linkBranchesMutation({
  environment,
  input: {baseIssueId, linkingIds},
  onError,
  linkingBranches,
}: LinkBranchesMutationParams) {
  return commitMutation<linkBranchesMutation>(environment, {
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    mutation: graphql`
      mutation linkBranchesMutation($baseIssueId: ID!, $linkingIds: [ID!]!) @raw_response_type {
        linkBranches(input: {issueId: $baseIssueId, linkingIds: $linkingIds}) {
          issue {
            ... on Issue {
              linkedBranches(first: 25) {
                nodes {
                  id
                  # Ref fields inlined rather than shared via a fragment
                  # because we're removing Relay and a graphql tagged query
                  # can't interpolate a shared selection string.
                  ref {
                    name
                    id
                    __typename
                    target {
                      oid
                      id
                      __typename
                    }
                    repository {
                      id
                      nameWithOwner
                      defaultBranchRef {
                        name
                        id
                        target {
                          oid
                          id
                          __typename
                        }
                        associatedPullRequests {
                          totalCount
                        }
                      }
                    }
                    associatedPullRequests {
                      totalCount
                    }
                  }
                }
              }
            }
          }
        }
      }
    `,
    variables: {baseIssueId, linkingIds},
    optimisticResponse: linkingBranches ? buildOptimisticResponse(baseIssueId, linkingBranches) : undefined,
    onError,
  })
}
